import * as _ from "lodash";
import * as moment from "moment";
import "moment-timezone";

import { APP_TIMEZONE } from "../../@core/utils/constants";
import { BaseDto } from "./base.dto";
import { FileDto } from "./file.dto";
import { PagingDto } from "./paging.dto";
import { TranslationDto } from "./translation.dto";

class MallDto {
  id?: string;
  name?: TranslationDto;
  slug?: string;
}

// mapping to form
export class EventMappingDto extends BaseDto {
  bannerImage?: FileDto;
  title?: TranslationDto;
  startDate?: string;
  endDate?: string;
  content?: TranslationDto;
  customerLimit?: number;
  displayStatus?: boolean;
  mallIds?: string[];

  static create({
    id,
    bannerImage,
    title,
    startDate,
    endDate,
    content,
    customerLimit,
    mallIds,
    displayStatus,
  }: EventMappingDto) {
    return _.assign<EventDto>({
      id: id || null,
      bannerImage: bannerImage || null,
      title: title || TranslationDto.create(),
      startDate: startDate || null,
      endDate: endDate || null,
      content: content || TranslationDto.create(),
      customerLimit: customerLimit || null,
      displayStatus: displayStatus || true,
      mallIds: mallIds || null,
    });
  }
}

// event response
export class EventDto extends BaseDto {
  mall?: MallDto[];
  bannerImage?: FileDto;
  title?: TranslationDto;
  startDate?: string;
  endDate?: string;
  content?: TranslationDto;
  customerLimit?: number;
  displayStatus?: boolean;
  customerRegister?: number;
  customerCheckedIn?: number;
  deepLink?: string;
  /**
   * FR-03-BR-11 (Stamp Collection) — Event đang được gán cho Milestone
   * "Check in events" của một Challenge. Dùng để chặn hoặc cảnh báo khi xoá.
   * TODO(MOCK): cần BE trả 2 field này; chưa có thì luồng xoá giữ nguyên như cũ.
   */
  linkedChallengeId?: string;
  /** Trạng thái Challenge đang liên kết: 'planned' | 'ongoing' | 'ended'. */
  linkedChallengeStatus?: string;
}

export class CustomerMallDto {
  id?: string;
  name?: TranslationDto;
}

export class CustomerDto extends BaseDto {
  fullName?: string;
  email?: string;
  phoneNumber?: string;
  eventTitle: TranslationDto;
  eventMallName: CustomerMallDto[];
  id: string;
}

/**
 * `activityStatus` — trạng thái theo thời gian của Event: `1 = expired` (đã diễn ra,
 * "Took place"), `2 = ongoing` ("Happening"), `3 = upcoming`. Trùng giá trị với
 * `ACTIVITY_STATUS` của màn Event list (`+event/event.constants.ts`).
 *
 * ⚠️ Đây là **query param** của `GET /admin/v1/events`, response **không** trả field này
 * (xem `EventDto`) — lọc bằng `row.activityStatus` sẽ luôn trúng `undefined`. Muốn biết
 * trạng thái của một row phải tính lại từ `startDate`/`endDate`:
 * `resolveEventActivityStatus()`.
 */
export const EVENT_ACTIVITY_STATUS = {
  EXPIRED: 1,
  ONGOING: 2,
  UPCOMING: 3,
} as const;

export type EventActivityStatus = typeof EVENT_ACTIVITY_STATUS[keyof typeof EVENT_ACTIVITY_STATUS];

/**
 * Bản số hoá của `getActivityStatus()` (`+event/event.utils.ts`) — cùng mốc so sánh nên
 * kết quả luôn khớp với chip trạng thái trên màn Event list.
 *
 * Event thiếu/sai `startDate`-`endDate` rơi vào `ONGOING`, giống hàm gốc: dữ liệu không đủ
 * để kết luận đã kết thúc.
 */
export function resolveEventActivityStatus(
  startDate?: string,
  endDate?: string
): EventActivityStatus {
  const today = moment().tz(APP_TIMEZONE);

  if (moment(startDate).tz(APP_TIMEZONE).isAfter(today)) {
    return EVENT_ACTIVITY_STATUS.UPCOMING;
  }

  if (moment(endDate).tz(APP_TIMEZONE).isBefore(today)) {
    return EVENT_ACTIVITY_STATUS.EXPIRED;
  }

  return EVENT_ACTIVITY_STATUS.ONGOING;
}

export class FindEventDto extends PagingDto {
  searchKey?: string;
  displayStatus?: boolean;
  activityStatus?: number;
  mallId?: string;
  mallIds?: string;
}

export class FindCustomerEventDto extends PagingDto {
  type?: string;
}

export class CreateEventDto {
  mallIds: string[];
  bannerImageId: string;
  title: TranslationDto;
  startDate: string;
  endDate: string;
  content: TranslationDto;
  customerLimit?: number;
  displayStatus: boolean;
}

export class UpdateEventDto extends CreateEventDto {
  id: string;
}
